import React, { useState } from 'react';
import { Button, Checkbox, Text } from 'src/@vymo/ui/atoms';
import { DiffEditor, Editor } from '@monaco-editor/react';
import { ReactComponent as LeftIcon } from 'src/assets/icons/left.svg';
import { ReactComponent as RightIcon } from 'src/assets/icons/right.svg';
import JSONControls from './controls';
import styles from './index.module.scss';

function JSONPlayground() {
  const [leftEditorValue, setLeftEditorValue] = useState<Record<string, any>>(
    {},
  );

  const [rightEditorValue, setRightEditorValue] = useState<Record<string, any>>(
    {},
  );

  const [leftEditorError, setLeftEditorError] = useState<string>('');
  const [rightEditorError, setRightEditorError] = useState<string>('');
  const [comparatorEnabled, setComparatorEnabled] = useState<boolean>(false);

  const handleEditorChange = (value: string | undefined, setter, error) => {
    let parsedValue: any = {};

    try {
      parsedValue = JSON.parse(value || '{}');
      error('');
      setter(parsedValue);
    } catch (e: any) {
      error(e.message || 'Invalid JSON');
    }
  };
  return (
    <div className={styles.jsonPlayground}>
      <div className={styles.jsonPlayground__header}>
        <Text type="h3">JSON Editor</Text>
        <Text type="description">
          This JSON editor uses the Monaco Editor and supports all Visual Studio
          Code shortcuts for a seamless editing experience.
        </Text>
      </div>
      <div className={styles.jsonPlayground__topControlsWrapper}>
        <JSONControls
          data={leftEditorValue}
          onChange={setLeftEditorValue}
          error={leftEditorError}
        />
        <JSONControls
          data={rightEditorValue}
          onChange={setRightEditorValue}
          error={rightEditorError}
        />
      </div>

      <div className={styles.jsonPlayground__errors}>
        <Text type="sublabel" variant="error">
          {leftEditorError}
        </Text>

        <Text type="sublabel" variant="error">
          {rightEditorError}
        </Text>
      </div>

      <div className={styles.jsonPlayground__editorWrapper}>
        {!comparatorEnabled && (
          <Editor
            height="100%"
            defaultLanguage="json"
            value={JSON.stringify(leftEditorValue, null, 4)}
            theme="vs"
            onChange={(val) =>
              handleEditorChange(val, setLeftEditorValue, setLeftEditorError)
            }
            loading="Loading JSON Editor..."
            options={{
              autoDetectHighContrast: true,
              scrollBeyondLastLine: false,
              fontSize: 12,
              folding: true,
            }}
            className={styles.jsonPlayground__editorWrapper__monacoEditorLeft}
          />
        )}

        {!comparatorEnabled && (
          <Editor
            height="100%"
            defaultLanguage="json"
            value={JSON.stringify(rightEditorValue, null, 4)}
            theme="vs"
            onChange={(val) =>
              handleEditorChange(val, setRightEditorValue, setRightEditorError)
            }
            loading="Loading JSON Editor..."
            options={{
              autoDetectHighContrast: true,
              scrollBeyondLastLine: false,
              fontSize: 12,
              folding: true,
            }}
            className={styles.jsonPlayground__editorWrapper__monacoEditorRight}
          />
        )}

        {comparatorEnabled && (
          <DiffEditor
            height="100%"
            original={JSON.stringify(leftEditorValue, null, 4)}
            modified={JSON.stringify(rightEditorValue, null, 4)}
            language="json"
            options={{
              renderSideBySide: true,
              originalEditable: false,
              scrollBeyondLastLine: false,
              readOnly: true,
              fontSize: 12,
              minimap: { enabled: true },
              folding: true,
            }}
            className={styles.jsonPlayground__editorWrapper__diffEditor}
          />
        )}

        <div className={styles.jsonPlayground__controls}>
          <Button
            type="text"
            onClick={() => setRightEditorValue(leftEditorValue)}
            className={styles.jsonPlayground__controls__btn}
            disabled={comparatorEnabled}
          >
            <RightIcon />
          </Button>{' '}
          <Checkbox
            label={
              <Text type="sublabel" bold>
                COMPARE
              </Text>
            }
            value={comparatorEnabled}
            size="small"
            onChange={(c) => setComparatorEnabled(c)}
            className={styles.jsonPlayground__controls__check}
          />
          <Button
            type="text"
            onClick={() => setLeftEditorValue(rightEditorValue)}
            className={styles.jsonPlayground__controls__btn}
            disabled={comparatorEnabled}
          >
            <LeftIcon />
          </Button>{' '}
        </div>
      </div>
    </div>
  );
}

export default JSONPlayground;
